import * as R from 'ramda';
import {CountryCode} from '@practicegenius/countries/src/types';
import {
  ApiClient,
  UnknownServerData,
} from '@practicegenius/http-clients-js/src/types';
import {objectKeysToCamelCaseDeep} from '@practicegenius/pg-util/src/ObjectUtils';
import {setType} from '@practicegenius/pg-util/src/TypescriptUtils';
import {pipe} from '@practicegenius/pg-util/src/FunctionUtils';
import {addQueryParams} from '@practicegenius/pg-util/src/UrlUtils';
import {parseIsoOrNull} from '@practicegenius/pg-util/src/DateUtils';
import {ServiceBrand} from './serviceBrand';
import {MembershipType} from './membershipType';

export type PracticeType = 'dental' | 'ortho' | 'education';
export type PracticeStatus =
  | 'live'
  | 'training'
  | 'postTraining'
  | 'demo'
  | 'disabled'
  | 'deleted';

export type PracticeFeature =
  | 'login_only'
  | 'user_parent_accounts'
  | 'communication_manager'
  | 'sites_accessible_by_team_member'
  | 'referral_manager'
  | 'promotions'
  | 'contests_beta'
  | 'duplicate_manager'
  | 'analytics_dummy_data'
  | 'global_user_beta'
  | 'home_page_alerts_beta'
  | 'automation_manager'
  | 'automation_manager_beta'
  | 'reviews_automation_beta'
  | 'instagram'
  | 'hubbux_teams_beta'
  | 'sweepstakes_beta'
  | 'registration_invite_beta'
  | 'stock_image_cms_beta'
  | 'reactions_comments_beta'
  | 'recognition_giphy_beta'
  | 'hide_non_admin_redeem_beta'
  | 'add_updated_members_table'
  | 'internal_analytics_beta'
  | 'try_members_app'
  | 'members_app_beta'
  | 'identity_service_beta'
  | 'generic_posts_beta'
  | 'global_staff_beta'
  | 'account_billing_beta'
  | 'alert_for_new_billing'
  | 'ai_scheduler_beta'
  | 'member_modern_login_beta'
  | 'ach_as_payment_method'
  | 'ach_rule_balance';

export type PracticeSegmentIdentifier =
  | 'dental'
  | 'ortho'
  | 'education'
  | 'medical'
  | 'physical_therapy'
  | 'teams'
  | 'chiropractic'
  | 'optometry';

export type PracticeSegment = {
  id: number;
  identifier: PracticeSegmentIdentifier;
  label: string;
};

type LinkedPracticeType = 'business' | 'teams' | 'partners';
type LinkedPractice = {
  /** practiceId of the linked practice */
  id: number;
  /** Fully Qualified logo to the URL image */
  logo: string;
  name: string;
  serviceBrand: ServiceBrand;
  membershipType: MembershipType;
  segment: PracticeSegment;
};

export type Practice = {
  id: number;
  branchName?: string;
  businessCode: string;
  uri: string;
  hubUrl: string;
  name: string;
  type: PracticeType;
  serviceBrand: ServiceBrand;
  membershipType: MembershipType;
  status: PracticeStatus;
  website: string;
  phone: string;
  email: string;
  fax?: string;
  timeZone: string;
  appointmentsEnabled: boolean;
  hasUploadedLogo: boolean;
  logo: string;
  logoAlternate: string;
  rewardCard: string;
  colors: {
    color1: string;
    color2: string;
    color3: string;
    color4: string;
    color5: string;
    color6: string;
    color7: string;
    color8: string;
    color9: string;
    color10: string;
    color11: string;
  };
  practiceFeatures: PracticeFeature[];
  hasPmsIntegration: boolean;
  segment: PracticeSegment;
  linkedPractices?: Partial<Record<LinkedPracticeType, LinkedPractice>>;
  address1?: string;
  address2?: string;
  city?: string;
  state?: string;
  zip?: string;
  country: CountryCode;
  software: string | null;
  startedAt: Date | null;
  isInternal: boolean;
};

export type PracticeOffice = {
  id: number;
  name: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  zip: string;
  country: CountryCode;
  phone: string;
  email: string;
  fax?: string;
};

export type PracticeLocation = PracticeOffice & {
  geometry: {
    location: {
      lat: number;
      lng: number;
    };
    viewport: {
      south: number;
      west: number;
      north: number;
      east: number;
    };
  };
};

export type PracticeReview = {
  id: number;
  reviewUrl: string;
  practiceId: number;
  locationId: number;
  userId: number;
  rating: number;
  message: string;
  featured: boolean;
  status: 'unpublished' | 'published';
  createdAt: Date;
};

export const transformPracticeOffices: (
  data: UnknownServerData,
) => PracticeOffice[] = R.pipe(
  R.pipe(R.propOr({}, 'items'), objectKeysToCamelCaseDeep),
  setType<PracticeOffice[]>(),
);

/** Pull linked practices from response meta and attach it to the main entity */
const parseLinkedPractices = (data: UnknownServerData) => {
  const {item, meta} = data;
  const linkedPractices: Practice['linkedPractices'] = {};

  if (meta?.teamAccount) linkedPractices.teams = meta.teamAccount;
  if (meta?.businessAccount) linkedPractices.business = meta.businessAccount;
  if (meta?.partnersAccount) linkedPractices.partners = meta.partnersAccount;

  return R.isEmpty(linkedPractices)
    ? item
    : {
        ...item,
        linkedPractices: objectKeysToCamelCaseDeep(linkedPractices),
      };
};

/** Transform a practice data object to the format expected by the client. Use this for
 *  parsing practice info provided via includes. Note that in this case linkedPractice info will never be included
 *  because it is only included as metadata in the main practice response */
export const transformPracticeData = pipe<
  Omit<Practice, 'linkedPractices'>,
  UnknownServerData
>(
  objectKeysToCamelCaseDeep,
  R.evolve({
    startedAt: parseIsoOrNull,
    isInternal: Boolean,
  }),
);

/** Transform a practice response object to the format expected by the client. Use this for
 *  parsing practice info provided in response to a direct request for practice info */
export const transformPracticeResponse = pipe<Practice, UnknownServerData>(
  R.pick(['item', 'meta']),
  R.evolve({item: transformPracticeData}),
  parseLinkedPractices,
);

export const fetchRawPractice =
  (coreAuth: ApiClient) =>
  ({practiceId}: {practiceId: number}) =>
    coreAuth._unsafe
      .get(`/practices/${practiceId}`)
      .then(x => x.data as UnknownServerData);

/** Is the practice of the given brand? */
export const practiceIsServiceBrand = R.curry(
  (serviceBrand: ServiceBrand, p: Practice) => p.serviceBrand === serviceBrand,
);
/** Is the practice of the given segment? */
export const practiceIsSegment = R.curry(
  (segment: PracticeSegmentIdentifier, p: Practice) =>
    p.segment.identifier === segment,
);
/** Does the practice have a linkage to another practice of the given type? */
export const practiceHasLinkToType = R.curry(
  (type: LinkedPracticeType, p: Practice) => Boolean(p.linkedPractices?.[type]),
);

/** Is the given practice a Teams account? */
export const practiceIsTeams = (
  practice: Practice,
): practice is Practice & {
  membershipType: 'employees';
} => practice.membershipType === 'employees';

/** Make a getter for the URL for a given practice's logo */
export type PracticeLogoUrlOptions = Partial<{
  width: number;
  pixelDensity: number;
  trimWhiteSpace: boolean;
}>;

export const makeGetPracticeLogoUrl =
  (config: {coreServiceUrl: string}) =>
  (practiceId: number, options: PracticeLogoUrlOptions = {}) => {
    const logoBaseUrl = `${config.coreServiceUrl}/practices/${practiceId}/logo`;

    if (R.isEmpty(options)) return logoBaseUrl;

    const params: Record<string, string | number> = {};
    if (options.width) {
      params.w = options.width;
      if (options.pixelDensity) params.w *= options.pixelDensity;
    }
    if (options.trimWhiteSpace) params.trimaway = 'true';

    return addQueryParams(
      `${config.coreServiceUrl}/practices/${practiceId}/logo`,
      params,
    );
  };
